import React, { useState, useEffect, useRef, useCallback } from 'react';

const PreviewRecordSelector = ({
  requiredContexts = [],
  onSelectionChange,
  selectedCompanyId,
  selectedEventId,
  selectedUserId
}) => {
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState(null);

  // Company search state
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const companySearchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    loadPreviewRecords();
  }, []);

  // Refetch events when company selection changes
  useEffect(() => {
    loadEvents(selectedCompanyId);
  }, [selectedCompanyId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (companySearchRef.current && !companySearchRef.current.contains(e.target)) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPreviewRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/email_templates/preview_records?limit=50');
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      setEvents(data.events || []);
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error loading preview records:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (companyId) => {
    setLoadingEvents(true);
    try {
      const url = companyId
        ? `/admin/email_templates/preview_records?limit=50&company_id=${companyId}`
        : '/admin/email_templates/preview_records?limit=50';
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      const newEvents = data.events || [];
      setEvents(newEvents);

      // Clear selected event if it's not in the new filtered list
      if (selectedEventId && !newEvents.find(e => String(e.id) === String(selectedEventId))) {
        onSelectionChange({
          companyId: selectedCompanyId,
          eventId: null,
          userId: selectedUserId
        });
      }
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const searchCompanies = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCompanyResults([]);
      return;
    }
    setSearchingCompanies(true);
    try {
      const response = await fetch(`/admin/email_templates/search_companies?q=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      setCompanyResults(data);
    } catch (error) {
      console.error('Error searching companies:', error);
      setCompanyResults([]);
    } finally {
      setSearchingCompanies(false);
    }
  }, []);

  const handleCompanySearchChange = (e) => {
    const value = e.target.value;
    setCompanySearch(value);
    setShowCompanyDropdown(true);

    // Debounce the search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchCompanies(value);
    }, 300);
  };

  const handleSelectCompany = (company) => {
    setSelectedCompanyName(company.name);
    setCompanySearch('');
    setCompanyResults([]);
    setShowCompanyDropdown(false);
    onSelectionChange({
      companyId: String(company.id),
      eventId: selectedEventId,
      userId: selectedUserId
    });
  };

  const handleClearCompany = () => {
    setSelectedCompanyName('');
    setCompanySearch('');
    setCompanyResults([]);
    setShowCompanyDropdown(false);
    onSelectionChange({
      companyId: null,
      eventId: selectedEventId,
      userId: selectedUserId
    });
  };

  const handleChange = (field, value) => {
    onSelectionChange({
      companyId: field === 'company' ? value : selectedCompanyId,
      eventId: field === 'event' ? value : selectedEventId,
      userId: field === 'user' ? value : selectedUserId
    });
  };

  // Always show Company and Event selectors (user can pick any, even if not used)
  // Hide User selector as it's rarely needed
  const showCompany = true;
  const showEvent = true;
  const showUser = false;

  if (loading) {
    return (
      <div className="preview-record-selector loading">
        <small className="text-muted">
          <i className="fa fa-spinner fa-spin mr-1"></i>
          Loading preview data...
        </small>
      </div>
    );
  }

  if (error) {
    return (
      <div className="preview-record-selector error">
        <small className="text-danger">
          <i className="fa fa-exclamation-triangle mr-1"></i>
          Failed to load preview data: {error}
        </small>
        <button
          className="btn btn-link btn-sm p-0 ml-2"
          onClick={loadPreviewRecords}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="preview-record-selector">
      <div className="selector-header">
        <small className="text-muted font-weight-bold">
          <i className="fa fa-database mr-1"></i>
          Preview Data
        </small>
      </div>

      <div className="selector-fields">
        {showCompany && (
          <div className="selector-field" ref={companySearchRef}>
            <label>
              <i className="fa fa-building mr-1"></i>
              Company
            </label>
            <div className="company-search-wrapper">
              {selectedCompanyName ? (
                <div className="company-selected">
                  <span className="company-selected-name">{selectedCompanyName}</span>
                  <button
                    className="company-clear-btn"
                    onClick={handleClearCompany}
                    title="Clear selection"
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  className="form-control company-search-input"
                  placeholder="Search companies..."
                  value={companySearch}
                  onChange={handleCompanySearchChange}
                  onFocus={() => {
                    if (companySearch.length >= 2) setShowCompanyDropdown(true);
                  }}
                />
              )}
              {showCompanyDropdown && (companyResults.length > 0 || searchingCompanies || companySearch.length >= 2) && (
                <div className="company-dropdown">
                  {searchingCompanies ? (
                    <div className="company-dropdown-item company-dropdown-loading">
                      <i className="fa fa-spinner fa-spin mr-1"></i>
                      Searching...
                    </div>
                  ) : companyResults.length > 0 ? (
                    companyResults.map(company => (
                      <div
                        key={company.id}
                        className="company-dropdown-item"
                        onClick={() => handleSelectCompany(company)}
                      >
                        {company.name}
                      </div>
                    ))
                  ) : companySearch.length >= 2 ? (
                    <div className="company-dropdown-item company-dropdown-empty">
                      No companies found
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}

        {showEvent && (
          <div className="selector-field">
            <label>
              <i className="fa fa-calendar mr-1"></i>
              Event
              {loadingEvents && <i className="fa fa-spinner fa-spin ml-1"></i>}
            </label>
            <select
              className="form-control"
              value={selectedEventId || ''}
              onChange={(e) => handleChange('event', e.target.value || null)}
              disabled={loadingEvents}
            >
              <option value="">
                {loadingEvents ? 'Loading events...' : 'Select event...'}
              </option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {showUser && (
          <div className="selector-field">
            <label>
              <i className="fa fa-user mr-1"></i>
              User
            </label>
            <select
              className="form-control"
              value={selectedUserId || ''}
              onChange={(e) => handleChange('user', e.target.value || null)}
            >
              <option value="">Select user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <style>{`
        .preview-record-selector {
          background: #f8f9fa;
          border-radius: 4px;
          padding: 12px;
        }

        .preview-record-selector.loading,
        .preview-record-selector.error {
          text-align: center;
          padding: 20px;
        }

        .preview-record-selector.error {
          background: #fff3cd;
          border: 1px solid #ffc107;
        }

        .selector-header {
          margin-bottom: 10px;
        }

        .selector-fields {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .selector-field {
          flex: 1;
          min-width: 150px;
          position: relative;
        }

        .selector-field label {
          display: block;
          font-size: 11px;
          font-weight: 500;
          color: #6c757d;
          margin-bottom: 4px;
        }

        .selector-field select {
          font-size: 13px;
          height: auto;
          min-height: 38px;
          padding: 6px 10px;
          line-height: 1.4;
        }

        /* Company search combobox */
        .company-search-wrapper {
          position: relative;
        }

        .company-search-input {
          font-size: 13px;
          min-height: 34px;
        }

        .company-selected {
          display: flex;
          align-items: center;
          background: #fff;
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 4px 8px;
          min-height: 34px;
          font-size: 13px;
        }

        .company-selected-name {
          flex: 1;
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          line-height: 1.3;
        }

        .company-clear-btn {
          background: none;
          border: none;
          color: #6c757d;
          font-size: 18px;
          line-height: 1;
          padding: 0 0 0 8px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .company-clear-btn:hover {
          color: #dc3545;
        }

        .company-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ced4da;
          border-top: none;
          border-radius: 0 0 4px 4px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .company-dropdown-item {
          padding: 6px 10px;
          font-size: 13px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
        }

        .company-dropdown-item:last-child {
          border-bottom: none;
        }

        .company-dropdown-item:hover {
          background: #e9ecef;
        }

        .company-dropdown-loading,
        .company-dropdown-empty {
          color: #6c757d;
          font-style: italic;
          cursor: default;
        }

        .company-dropdown-loading:hover,
        .company-dropdown-empty:hover {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default PreviewRecordSelector;
